
import { Inject, Injectable, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { environment } from 'src/environments/environment';

type FinancialAdminElement = 'sale-item' | 'sale-item-selector';

declare global {
  interface Window {
    // FAUL exposes this exact global as part of its public asset-loading contract.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    SeFinancialAdminUi?: {
      load: (element: FinancialAdminElement) => Promise<void>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class LoadScriptsService {
  private renderer2: Renderer2;
  private scriptPromises = new Map<string, Promise<void>>();

  constructor(
    private rendererFactory2: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer2 = rendererFactory2.createRenderer(null, null);
  }

  public async loadScripts(elementScript: string): Promise<void> {
    const element = this.elementName(elementScript);
    const scripts = ['polyfills', 'main'];
    for (const src of scripts) {
      await this.injectScript(src);
    }

    const loader = window.SeFinancialAdminUi?.load;
    if (!loader) {
      throw new Error('Financial Admin UI loader was not initialized by main.js');
    }

    await loader(element);
  }

  private elementName(elementScript: string): FinancialAdminElement {
    const elements: Record<string, FinancialAdminElement> = {
      saleItem: 'sale-item',
      saleItemSelector: 'sale-item-selector',
    };
    const element = elements[elementScript];

    if (!element) {
      throw new Error(`Unsupported Financial Admin UI element: ${elementScript}`);
    }

    return element;
  }

  private async injectScript(src: string): Promise<void> {
    const id = 'se-financial-admin-' + src;
    const financialLibScript = this.document.getElementById(id) as HTMLScriptElement | null;
    if (financialLibScript) {
      await (this.scriptPromises.get(id) || Promise.resolve());
      return;
    }

    const scriptPromise = new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = `${environment.financialAdminLibraryAssetsUrl}/${src}.js`;
      script.type = 'module';
      script.id = id;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Unable to load ${script.src}`));
      this.renderer2.appendChild(this.document.body, script);
    });
    this.scriptPromises.set(id, scriptPromise);
    await scriptPromise;
  }
}
